// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import "es6-promise/auto";
import Enumerable from "linq";
import sanitizeHtml from "sanitize-html";
import Vue from "vue";
import Router from "vue-router";
import { Store } from "vuex";
import { State } from "../../store/state";
import { SessionStorageAccessor } from "../../store/SessionStorageAccessor";
import { IConfiguration, IWordingConfiguration } from "../../domain/configuration";
import { Size, TextStyle, Rectangle } from "../../domain/drawing";
import { DocumentKind, IDocumentImageSource } from "../../domain/document";
import { IRectangle, StringUtility } from "../../domain/system";
import { Department } from "../../domain/department";
import { User, Profession } from "../../domain/user";
import { LoggerFactory, ILogger } from "../../utilities/logger";
import { App } from "../../components";
import {
  DeveloperDialog,
} from "../../components/developer";
import {
  DepartmentButtonSelectorDialog,
  DepartmentListSelectorDialog,
  MultiDepartmentListSelectorDialog,
  MultiOrderableDepartmentListSelectorDialog,
  DepartmentGroupButtonSelectorDialog,
  DepartmentGroupListSelectorDialog,
} from "../../components/department";
import {
  DepartmentButtonSelectorDialog as WizardDepartmentButtonSelectorDialog,
} from "../../components/department/dialogs/wizard/guest";
import {
  DocumentSelectDialog,
  DocumentNoteDialog,
  EditConfirmDialog,
  IEditConfirmableAsynchronously,
  MultiOrderableDocumentKindListSelectorDialog,
  MultiDocumentKindListSelectorDialog,
  IDocumentKindListSelectorDialogCondition,
} from "../../components/document/dialogs";
import {
  EditConfirmDialog as WizardEditConfirmDialog,
} from "../../components/document/dialogs/wizard/guest";
import {
  instanceOfIImageErasable,
  ImageEraser,
} from "../../components/drawing";
import {
  MultiOrderableProfessionListSelectorDialog,
  IProfessionListSelectorDialogCondition,
} from "../../components/profession/dialogs";
import {
  MultiOrderableProfessionListSelectorDialog as WizardMultiOrderableProfessionListSelectorDialog,
} from "../../components/profession/dialogs/wizard/guest";
import {
  MultiOrderableUserListSelectorDialog,
  UserListSelectorDialog,
  IUserListSelectorDialogCondition,
} from "../../components/user/dialogs";
import {
  UserListSelectorDialog as WizardUserListSelectorDialog,
  MultiOrderableUserListSelectorDialog as WizardMultiOrderableUserListSelectorDialog,
} from "../../components/user/dialogs/wizard/guest";
import { MultiFormatListSelectorDialog, PreviewedFormatSelectorInstantDialog, FormatListSelectorDialog } from "../../components/format/dialogs";
import {
  FixedPhraseEditDialog,
} from "../../components/document/fixedPhrase/dialogs";
import { PatientFindDialog } from "../../components/patient/dialogs";
import {
  instanceOfITextSizeMeasure,
  instanceOfISourcePrintable,
  instanceOfISourcePrinterSpecifiable,
  PdfPrinter,
  instanceOfITextInputContextMenu,
  TextInputContextMenu,
  InvisibleController,
  IDialogCondition,
} from "../../components/system";
import TextSizeMeasure from "../../components/system/TextSizeMeasure.vue";
import Tooltip from "../../components/system/Tooltip.vue";
import NotImplemented from "../../components/system/NotImplementedComponent.vue";
import {
  ConfirmDialog,
  DateSelectDialog,
  IConfirmableAsynchronously,
} from "../../components/system/dialogs";
import {
  ConfirmDialog as WizardConfirmDialog,
} from "../../components/system/dialogs/wizard/guest";
import {
  ISingleSelectableAsynchronously,
  ISingleSelectableConditionallyAsynchronously,
  IMultiSelectableAsynchronously,
  IMultiSelectableConditionallyAsynchronously,
} from "../../components/system/selectors";
import { GeometryManager, IGeometryManageable } from "../../components/system/utilities";
import { WindowOpener } from "../../components/system/utilities/WindowOpener";
import { load } from "../../forked-libs/element-ui/load";
import { load as loadQuill } from "../../forked-libs/quill/load";
import { WindowCloser } from "../../system";
import { AsyncUtility } from "../../utilities";
import { AuthenticationDialog, IAuthenticatableAsynchronously } from "../../components/authentication/dialogs";
import { AuthenticationDialog as WizardAuthenticationDialog } from "../../components/authentication/dialogs/wizard/guest";
import { PostalAddressPickerDialog } from "../../components/contact/dialogs";
import { ConflictCommentComparedDialog } from "../../components/workflow/dialogs";

export class VueLoader {
  static load(store: Store<State>, router: Router): void {
    this.loadConfiguration();
    this.loadPlugins();

    this.loadComponents();
    this.loadDirectives();
    this.loadMethods(store, () => this.vm);
    this.loadProperties(() => this.vm);
    this.vm = this.createApplication(store, router);
  }

  private static vm: Vue;

  private static loadConfiguration(): void {
    Vue.config.productionTip = false;
    // Vue.config.devtools = true;  // デフォルトはtrue.(プロダクションビルドではfalse.)
    Vue.config.performance = Vue.config.devtools; // パフォーマンスもdevtoolsに合わせる.
  }

  private static loadPlugins(): void {
    // プラグイン等グローバル設定.(App.vueの中に実装するとmixin等で機能が使えないのでグローバルスコープで.)
    Vue.use(Router);
    
    // import ElementUI from "element-ui";
    // Vue.use(ElementUI);
    // import "./forked-libs/element-ui/lib/theme-standard/index.scss"; // themeはローカルの物を参照.
    // ElementUI丸ごとVue.useするとファイルサイズが大きくなるので、個別でimportする対応をしている。
    // tsファイル内からだとbabel-plugin-componentが動作しない？模様なので、jsファイルを経由している。
    // https://github.com/ElementUI/babel-plugin-component
    // https://b.0218.jp/20180512151459.html
    load(Vue);

    // Quillの読み込み
    loadQuill();

    // サニタイズメソッド追加
    Vue.prototype.$sanitizeHtml = sanitizeHtml;
  }

  private static loadComponents(): void {
    // グローバルコンポーネント.
    Vue.component("tooltip", Tooltip);
    Vue.component("notImplemented", NotImplemented);
  }

  private static loadDirectives(): void {
    // カスタムディレクティブ追加.
    // IE固有の問題の暫定対処として実装してたが、この実装がDocumentEditorにあるとページ遷移時にメモリリークする問題が発生.
    // componentUpdated()の中の実装を空にしてもリークしたため、IEではカスタムディレクティブでHTMLElementを操作するの自体が危険？
    // 再度検証した所、元々の現象が再現できなかったため、呼び出し元で呼ばないように修正済.
    // カスタムディレクティブ実装時は、本問題を踏まえて実装を検討する事.
    // import { LoadingRecovering } from "./directives";
    // Vue.directive("loading-recovering", new LoadingRecovering());
  }

  private static loadMethods(store: Store<State>, getVm: () => Vue): void {
    // カスタムメソッド追加.
    Vue.prototype.$openWindow = function(location: string, parameters: { [key: string]: string } = {}): void {
      WindowOpener.open(this.$route.path, location, parameters);
    };
    Vue.prototype.$requestOpenWindow = function(location: string, parameters: { [key: string]: string } = {}): void {
      WindowOpener.requestOpen(location, parameters);
    };
    Vue.prototype.$wait = async function(isCompleted: () => boolean, pollingIntervalAsMsec: number = 100) {
      await AsyncUtility.wait(isCompleted, pollingIntervalAsMsec);
    };
    Vue.prototype.$sleep = async function(ms: number) {
      await AsyncUtility.sleep(ms);
    };
    Vue.prototype.$success = function(title: string, message: string, onClose: () => void = undefined): void {
      console.log(title);
      console.log(message);
      setTimeout(() => {
        this.$notify.success({
          title,
          message,
          position: store.getters.configuration.system.notification.position,
          duration: 3000,
          onClose,
        });
      }, 1);
    };
    Vue.prototype.$successAsHTML = function(title: string, message: string): void {
      console.log(title);
      console.log(message);
      setTimeout(() => {
        this.$notify.success({
          title,
          message,
          dangerouslyUseHTMLString: true,
          position: store.getters.configuration.system.notification.position,
          duration: 3000,
        });
      }, 1);
    };
    Vue.prototype.$info = function(title: string, message: string): void {
      console.info(title);
      console.info(message);
      setTimeout(() => {
        this.$notify.info({
          title,
          message,
          position: store.getters.configuration.system.notification.position,
          duration: 0
        });
      }, 1);
    };
    Vue.prototype.$infoAsHTML = function(title: string, message: string): void {
      console.info(title);
      console.info(message);
      setTimeout(() => {
        this.$notify.info({
          title,
          message,
          dangerouslyUseHTMLString: true,
          position: store.getters.configuration.system.notification.position,
          duration: 0,
        });
      }, 1);
    };
    Vue.prototype.$warn = function(title: string, message: string, onClose: () => void = undefined): void {
      console.warn(title);
      console.warn(message);
      setTimeout(() => {
        this.$notify({
          title,
          message,
          position: store.getters.configuration.system.notification.position,
          duration: 5000,
          onClose,
          type: "warning",
        });
      }, 1);
    };
    Vue.prototype.$error = function(title: string, message: string, reason: any = undefined): void {
      console.error(title);
      console.error(message);
      if (reason) {
        console.error(reason);
      }
      setTimeout(() => {
        this.$notify.error({
          title,
          message,
          position: store.getters.configuration.system.notification.position,
          duration: 0
        });
      }, 1);
    };
    Vue.prototype.$errorAsHTML = function(title: string, message: string, reason: any = undefined): void {
      console.error(title);
      console.error(message);
      if (reason) {
        console.error(reason);
      }
      setTimeout(() => {
        this.$notify.error({
          title,
          message,
          dangerouslyUseHTMLString: true,
          position: store.getters.configuration.system.notification.position,
          duration: 0
        });
      }, 1);
    };
    Vue.prototype.$fatal = function(title: string, message: string, reason: any = undefined, timeoutAsMsec: number = 0): void {
      console.error(title);
      console.error(message);
      if (reason) {
        console.error(reason);
      }
      this.$router.replace({
        path: "/Error",
        query: {
          url: location.href,
          title,
          message,
          timeoutAsMsec,
        },
      });
    };
    Vue.prototype.$suspend = function(title: string, message: string, reason: any = undefined, timeoutAsMsec: number = 0): void {
      console.warn(title);
      console.warn(message);
      if (reason) {
        console.warn(reason);
      }
      this.$router.replace({
        path: "/Warning",
        query: {
          url: location.href,
          title,
          message,
          timeoutAsMsec,
        },
      });
    };
    Vue.prototype.$notImplemented = function(message: string): void {
      console.error("未実装です。");
      setTimeout(() => {
        this.$notify.error({
          title: "未実装です。",
          message: !!message ? message : "実装したらこの通知の実装は削除してください。",
          position: store.getters.configuration.system.notification.position,
          duration: 3000
        });
      }, 1);
    };
    Vue.prototype.$measureTextDrawnSize = function(text: string, textStyle: TextStyle): Size {
      const vm = getVm();
      return instanceOfITextSizeMeasure(vm.$refs.textSizeMeasure)
        ? vm.$refs.textSizeMeasure.measure(text, textStyle)
        : new Size();
    };
    Vue.prototype.$printPdf = function(source: string, copies?: number, shouldSelectPrinter?: boolean, shouldPrintWatermarkForcibly?: boolean, launchRequestKey?: string): void {
      const vm = getVm();
      if (instanceOfISourcePrintable(vm.$refs.pdfPrinter)) {
        vm.$refs.pdfPrinter.printSource(source, copies, shouldSelectPrinter, shouldPrintWatermarkForcibly, launchRequestKey);
      }
    };
    Vue.prototype.$printPdfTo = function(source: string, printerName: string, copies?: number, launchRequestKey?: string): void {
      const vm = getVm();
      if (instanceOfISourcePrinterSpecifiable(vm.$refs.pdfPrinter)) {
        vm.$refs.pdfPrinter.printSourceTo(source, printerName, copies, launchRequestKey);
      }
    };
    Vue.prototype.$findAncestor = function(predicate: (s: Vue) => boolean): Vue {
      let ancestor = null as Vue;
      for (ancestor = this.$parent; (ancestor && !predicate(ancestor)); ancestor = ancestor.$parent) {
      }
      return ancestor;
    };
    Vue.prototype.$doWhenValid = function<T>(process: (i: T) => void, instance: T, ...instances: any[]): void {
      if (instance && Enumerable.from(instances).all(i => !!i)) {
        process(instance);
      }
    };
    Vue.prototype.$registerCloseConfirmation = function(confirm: () => Promise<boolean>): void {
      WindowCloser.closeConfirmation = confirm;
    };
    Vue.prototype.$registerDocumentRegisterAsTemporaryBeforeCloseHandler = function(handler: (isForcibly: boolean) => Promise<void>): void {
      WindowCloser.registerDocumentAsTemporaryBeforeCloseHandler = handler;
    };
    Vue.prototype.$closeWindow = async function(isForcibly: boolean = false) {
      if (isForcibly || await WindowCloser.closeConfirmation()) {
        await WindowCloser.closeHandler();
        WindowCloser.closeSelf();
      }
    };
    Vue.prototype.$registerCloseHandler = async function(process: () => Promise<void>): Promise<void> {
      const currentCloseHandler = WindowCloser.closeHandler;
      WindowCloser.closeHandler = async () => {
        await currentCloseHandler();
        await process();
      };
    };
    Vue.prototype.$listenGeometryHandling = function(listener: (names: string[]) => void): void {
      return GeometryManager.listenManagers(listener);
    };
    Vue.prototype.$storeGeometry = async function(): Promise<void> {
      try {
        await GeometryManager.store();
        this.$success(StringUtility.convertKanaFromHalfToFull(store.getters.configuration.system.window.restorer.label), 
          StringUtility.convertKanaFromHalfToFull(store.getters.configuration.system.window.restorer.label) + "しました");
      } catch (e) {
        this.$error(StringUtility.convertKanaFromHalfToFull(store.getters.configuration.system.window.restorer.label), 
          StringUtility.convertKanaFromHalfToFull(store.getters.configuration.system.window.restorer.label) + "に失敗しました", e);
      }
    };
    Vue.prototype.$registerGeometryManager = async function<T extends IRectangle>(manager: IGeometryManageable<T>): Promise<void> {
      await GeometryManager.registerManager(manager);
    };
    Vue.prototype.$unregisterGeometryManager = async function<T extends IRectangle>(manager: IGeometryManageable<T>): Promise<void> {
      await GeometryManager.unregisterManager(manager);
    };
    Vue.prototype.$eraseImage = async function(rectangle: Rectangle, scale: number, source: string, hiddenElement: HTMLElement): Promise<IDocumentImageSource> {
      const vm = getVm();
      return instanceOfIImageErasable(vm.$refs.imageEraser)
        ? vm.$refs.imageEraser.erase(rectangle, scale, source, hiddenElement)
        : null;
    };
    Vue.prototype.$registerErasingChangedHandler = async function(process: (isErasing: boolean) => Promise<void>): Promise<void> {
      const vm = getVm();
      return instanceOfIImageErasable(vm.$refs.imageEraser)
        ? vm.$refs.imageEraser.registerErasingChangedHandler(process)
        : null;
    };
    Vue.prototype.$showTextInputContextMenu = function(event: PointerEvent): void {
      const vm = getVm();
      if (instanceOfITextInputContextMenu(vm.$refs.textInputContextMenu)) {
        vm.$refs.textInputContextMenu.show(event);
      }
    };
    Vue.prototype.$redirect = function(path: string, query?: { [key: string]: any }): void {
      const formatted = Enumerable.from(query).where(p => !!p.value).select(p => p.key + "=" + p.value).toArray().join("&");
      this.$router.replace({
        path: "/Redirect",
        replace: true,
        query: {
          redirect: path + (formatted ? ("?" + formatted) : ""),
        },
      });
    };
    Vue.prototype.$redirectEntryRoute = function(editPath?: (p: string) => string): void {
      const redirect = editPath
        ? editPath(SessionStorageAccessor.entryFullPath)
        : SessionStorageAccessor.entryFullPath;
      if (!!redirect) {
        this.$router.replace({
          path: "/Redirect",
          replace: true,
          query: {
            redirect,
          },
        });
      }
    };
  }

  private static loadProperties(getVm: () => Vue): void {
    Vue.prototype.$logger = LoggerFactory.getLogger();
    Object.defineProperty(Vue.prototype, "$configuration", {
      get: function $configuration(): IConfiguration {
        return this.$store.getters.configuration as IConfiguration;
      }
    });
    Object.defineProperty(Vue.prototype, "$commonDialogs", {
      get: function $commonDialogs(): CommonDialogs {
        const vm = getVm();
        return new CommonDialogs(vm);
      }
    });
    Object.defineProperty(Vue.prototype, "$wording", {
      get: function $wording(): IWordingConfiguration {
        return this.$store.getters.configuration.wording as IWordingConfiguration;
      }
    });
  }

  private static createApplication(store: Store<State>, router: Router): Vue {
    return new Vue({
      el: "#app",
      router,
      store,
      components: {
        App,
        TextSizeMeasure,
        PdfPrinter,
        ConfirmDialog,
        WizardConfirmDialog,
        DocumentSelectDialog,
        PreviewedFormatSelectorInstantDialog,
        FixedPhraseEditDialog,
        DocumentNoteDialog,
        EditConfirmDialog,
        WizardEditConfirmDialog,
        MultiOrderableDocumentKindListSelectorDialog,
        MultiDocumentKindListSelectorDialog,
        DepartmentButtonSelectorDialog,
        WizardDepartmentButtonSelectorDialog,
        PatientFindDialog,
        DeveloperDialog,
        FormatListSelectorDialog,
        MultiOrderableUserListSelectorDialog,
        WizardMultiOrderableUserListSelectorDialog,
        MultiFormatListSelectorDialog,
        DepartmentListSelectorDialog,
        MultiDepartmentListSelectorDialog,
        MultiOrderableDepartmentListSelectorDialog,
        AuthenticationDialog,
        WizardAuthenticationDialog,
        ImageEraser,
        TextInputContextMenu,
        DateSelectDialog,
        UserListSelectorDialog,
        WizardUserListSelectorDialog,
        MultiOrderableProfessionListSelectorDialog,
        WizardMultiOrderableProfessionListSelectorDialog,
        InvisibleController,
        PostalAddressPickerDialog,
        DepartmentGroupButtonSelectorDialog,
        DepartmentGroupListSelectorDialog,
        ConflictCommentComparedDialog,
      },
      template:
        "<app>" +
          "<template v-slot:visual>" + 
            "<text-input-context-menu ref='textInputContextMenu'/>" + 
          "</template>" + 
          "<text-size-measure ref='textSizeMeasure'/>" +
          "<pdf-printer ref='pdfPrinter'/>" +
          "<confirm-dialog ref='confirmation'/>" +
          "<wizard-confirm-dialog ref='wizardConfirmation'/>" +
          "<document-select-dialog ref='documentSelector'/>" +
          "<previewed-format-selector-instant-dialog ref='previewedFormatSelector'/>" +
          "<fixed-phrase-edit-dialog ref='fixedPhraseEditor'/>" +
          "<document-note-dialog ref='documentNoteInput'/>" +
          "<edit-confirm-dialog ref='editConfirmation'/>" +
          "<wizard-edit-confirm-dialog ref='wizardEditConfirmation'/>" +
          "<multi-orderable-document-kind-list-selector-dialog ref='multiOrderableDocumentKindListSelector'/>" +
          "<multi-document-kind-list-selector-dialog ref='multiDocumentKindListSelector'/>" +
          "<department-button-selector-dialog ref='departmentButtonSelector'/>" +
          "<wizard-department-button-selector-dialog ref='wizardDepartmentButtonSelector'/>" +
          "<patient-find-dialog ref='patientFinder' name='患者'/>" + 
          "<developer-dialog ref='developerEditor'/>" + 
          "<format-list-selector-dialog ref='formatListSelector'/>" +
          "<multi-orderable-user-list-selector-dialog ref='multiOrderableUserListSelector'/>" +
          "<wizard-multi-orderable-user-list-selector-dialog ref='wizardMultiOrderableUserListSelector'/>" +
          "<multi-format-list-selector-dialog ref='multiFormatListSelector'/>" +
          "<department-list-selector-dialog ref='departmentListSelector'/>" +
          "<multi-department-list-selector-dialog ref='multiDepartmentListSelector'/>" +
          "<multi-orderable-department-list-selector-dialog ref='multiOrderableDepartmentListSelector'/>" +
          "<authentication-dialog ref='authenticator'/>" +
          "<wizard-authentication-dialog ref='wizardAuthenticator'/>" +
          "<image-eraser ref='imageEraser'/>" + 
          "<date-select-dialog ref='dateSelector'/>" +
          "<user-list-selector-dialog ref='userListSelector'/>" +
          "<wizard-user-list-selector-dialog ref='wizardUserListSelector'/>" +
          "<multi-orderable-profession-list-selector-dialog ref='multiOrderableProfessionListSelector'/>" +
          "<wizard-multi-orderable-profession-list-selector-dialog ref='wizardMultiOrderableProfessionListSelector'/>" +
          "<invisible-controller/>" +
          "<postal-address-picker-dialog ref='postalAddressPickerDialog'/>" + 
          "<department-group-button-selector-dialog ref='departmentGroupButtonSelector'/>" + 
          "<department-group-list-selector-dialog ref='departmentGroupListSelector'/>" + 
          "<conflict-comment-compared-dialog ref='conflictCommentComparedDialog'/>" + 
        "</app>"
    });
  }
}

class CommonDialogs {
  constructor(source: Vue) {
    this._source = source;
    this._confirmation = {
      default: this._source.$refs.confirmation as ConfirmDialog,
      mobile: this._source.$refs.wizardConfirmation as WizardConfirmDialog,
    };
    this.document = new DocumentCommonDialogs(source);
    this.documentKind = new DocumentKindCommonDialogs(source);
    this.format = new FormatCommonDialogs(source);
    this.department = new DepartmentCommonDialogs(source);
    this.patient = new PatientCommonDialogs(source);
    this.developer = new DeveloperDialogs(source);
    this.user = new UserCommonDialogs(source);
    this.authentication = new  AuthenticationDialogs(source);
    this.date = new DateSelectDialogs(source);
    this.profession = new ProfessionCommonDialogs(source);
    this.postalAddress = new PostalAddressDialogs(source);
    this.workflow = new WorkflowDialogs(source);
  }
  public readonly _source: Vue;
  public readonly document: DocumentCommonDialogs;
  public readonly documentKind: DocumentKindCommonDialogs;
  public readonly format: FormatCommonDialogs;
  public readonly department: DepartmentCommonDialogs;
  public readonly patient: PatientCommonDialogs;
  public readonly developer: DeveloperDialogs;
  public readonly user: UserCommonDialogs;
  public readonly authentication: AuthenticationDialogs;
  public readonly date: DateSelectDialogs;
  public readonly profession: ProfessionCommonDialogs;
  public readonly postalAddress: PostalAddressDialogs;
  public readonly workflow: WorkflowDialogs;

  public readonly _confirmation: {
    default: ConfirmDialog,
    mobile: WizardConfirmDialog,
  };

  get confirmation(): IConfirmableAsynchronously {
    return this._source.$route.meta.isMobile
      ? this._confirmation.mobile
      : this._confirmation.default;
  }
}

class DocumentCommonDialogs {
  constructor(source: Vue) {
    this._source = source;
    this.selector = source.$refs.documentSelector as DocumentSelectDialog;
    this.fixedPhraseEditor = source.$refs.fixedPhraseEditor as FixedPhraseEditDialog;
    this.noteInput = source.$refs.documentNoteInput as DocumentNoteDialog;
    this._editConfirmation = {
      default: this._source.$refs.editConfirmation as EditConfirmDialog,
      mobile: this._source.$refs.wizardEditConfirmation as WizardEditConfirmDialog,
    };
  }
  public readonly _source: Vue;
  public readonly selector: DocumentSelectDialog;
  public readonly fixedPhraseEditor: FixedPhraseEditDialog;
  public readonly noteInput: DocumentNoteDialog;

  public readonly _editConfirmation: {
    default: EditConfirmDialog,
    mobile: WizardEditConfirmDialog,
  };

  get editConfirmation(): IEditConfirmableAsynchronously {
    return this._source.$route.meta.isMobile
      ? this._editConfirmation.mobile
      : this._editConfirmation.default;
  }
}

class DocumentKindCommonDialogs {
  constructor(source: Vue) {
    this._source = source;
    this._multiOrderableListSelectors = {
      default: this._source.$refs.multiOrderableDocumentKindListSelector as MultiOrderableDocumentKindListSelectorDialog,
    };
    this.multiListSelector = this._source.$refs.multiDocumentKindListSelector as MultiDocumentKindListSelectorDialog;
  }

  public readonly _source: Vue;
  public readonly _multiOrderableListSelectors: {
    default: MultiOrderableDocumentKindListSelectorDialog,
  };
  public readonly multiListSelector: MultiDocumentKindListSelectorDialog;

  get multiOrderableListSelector(): IMultiSelectableAsynchronously<DocumentKind> & IMultiSelectableConditionallyAsynchronously<DocumentKind, IDocumentKindListSelectorDialogCondition> {
    return this._multiOrderableListSelectors.default;
  }
}

class FormatCommonDialogs {
  constructor(source: Vue) {
    this.selector = source.$refs.previewedFormatSelector as PreviewedFormatSelectorInstantDialog;
    this.listSelector = source.$refs.formatListSelector as FormatListSelectorDialog;
    this.multiListSelector = source.$refs.multiFormatListSelector as MultiFormatListSelectorDialog;
  }
  public readonly selector: PreviewedFormatSelectorInstantDialog;
  public readonly listSelector: FormatListSelectorDialog;
  public readonly multiListSelector: MultiFormatListSelectorDialog;
}

class DepartmentCommonDialogs {
  constructor(source: Vue) {
    this._source = source;
    this._buttonSelector = {
      default: this._source.$refs.departmentButtonSelector as DepartmentButtonSelectorDialog,
      mobile: this._source.$refs.wizardDepartmentButtonSelector as WizardDepartmentButtonSelectorDialog,
    };
    this.listSelector = source.$refs.departmentListSelector as DepartmentListSelectorDialog;
    this.multiListSelector = source.$refs.multiDepartmentListSelector as MultiDepartmentListSelectorDialog;
    this.multiOrderableListSelector = source.$refs.multiOrderableDepartmentListSelector as MultiOrderableDepartmentListSelectorDialog;
    this.groupButtonSelector = source.$refs.departmentGroupButtonSelector as DepartmentGroupButtonSelectorDialog;
    this.groupListSelector = source.$refs.departmentGroupListSelector as DepartmentGroupListSelectorDialog;
  }
  public readonly _source: Vue;
  public readonly _buttonSelector: {
    default: DepartmentButtonSelectorDialog,
    mobile: WizardDepartmentButtonSelectorDialog,
  };
  public readonly listSelector: DepartmentListSelectorDialog;
  public readonly multiListSelector: MultiDepartmentListSelectorDialog;
  public readonly multiOrderableListSelector: MultiOrderableDepartmentListSelectorDialog;
  public readonly groupButtonSelector: DepartmentGroupButtonSelectorDialog;
  public readonly groupListSelector: DepartmentGroupListSelectorDialog;

  get buttonSelector(): ISingleSelectableAsynchronously<Department> & ISingleSelectableConditionallyAsynchronously<Department, IDialogCondition> {
    return this._source.$route.meta.isMobile
      ? this._buttonSelector.mobile
      : this._buttonSelector.default;
  }
}

class PatientCommonDialogs {
  constructor(source: Vue) {
    this.finder = source.$refs.patientFinder as PatientFindDialog;
  }
  public readonly finder: PatientFindDialog;
}

class DeveloperDialogs {
  constructor(source: Vue) {
    this.editor = source.$refs.developerEditor as DeveloperDialog;
  }
  public readonly editor: DeveloperDialog;
}

class UserCommonDialogs {
  constructor(source: Vue) {
    this._source = source;
    this._userListSelectors = {
      default: this._source.$refs.userListSelector as UserListSelectorDialog,
      mobile: this._source.$refs.wizardUserListSelector as WizardUserListSelectorDialog,
    };
    this._multiOrderableListSelectors = {
      default: this._source.$refs.multiOrderableUserListSelector as MultiOrderableUserListSelectorDialog,
      mobile: this._source.$refs.wizardMultiOrderableUserListSelector as WizardMultiOrderableUserListSelectorDialog,
    };
  }
  public readonly _source: Vue;
  public readonly _userListSelectors: {
    default: UserListSelectorDialog,
    mobile: WizardUserListSelectorDialog,
  };

  public readonly _multiOrderableListSelectors: {
    default: MultiOrderableUserListSelectorDialog,
    mobile: WizardMultiOrderableUserListSelectorDialog,
  };

  get userListSelector(): ISingleSelectableAsynchronously<User> & ISingleSelectableConditionallyAsynchronously<User, IUserListSelectorDialogCondition> {
    return this._source.$route.meta.isMobile
      ? this._userListSelectors.mobile
      : this._userListSelectors.default;
  }

  get multiOrderableListSelector(): IMultiSelectableAsynchronously<User> & IMultiSelectableConditionallyAsynchronously<User, IUserListSelectorDialogCondition> {
    return this._source.$route.meta.isMobile
      ? this._multiOrderableListSelectors.mobile
      : this._multiOrderableListSelectors.default;
  }
}

class AuthenticationDialogs {
  constructor(source: Vue) {
    this._source = source;
    this._authenticator = {
      default: this._source.$refs.authenticator as AuthenticationDialog,
      mobile: this._source.$refs.wizardAuthenticator as WizardAuthenticationDialog,
    };
  }
  public readonly _source: Vue;
  public readonly _authenticator: {
    default: AuthenticationDialog,
    mobile: WizardAuthenticationDialog,
  };

  get authenticator(): IAuthenticatableAsynchronously {
    return this._source.$route.meta.isMobile
      ? this._authenticator.mobile
      : this._authenticator.default;
  }
}

class DateSelectDialogs {
  constructor(source: Vue) {
    this.dateSelector = source.$refs.dateSelector as DateSelectDialog;
  }
  public readonly dateSelector: DateSelectDialog;
}

class ProfessionCommonDialogs {
  constructor(source: Vue) {
    this._source = source;
    this._multiOrderableListSelectors = {
      default: this._source.$refs.multiOrderableProfessionListSelector as MultiOrderableProfessionListSelectorDialog,
      mobile: this._source.$refs.wizardMultiOrderableProfessionListSelector as WizardMultiOrderableProfessionListSelectorDialog,
    };
  }

  public readonly _source: Vue;
  public readonly _multiOrderableListSelectors: {
    default: MultiOrderableProfessionListSelectorDialog,
    mobile: WizardMultiOrderableProfessionListSelectorDialog,
  };

  get multiOrderableListSelector(): IMultiSelectableAsynchronously<Profession> & IMultiSelectableConditionallyAsynchronously<Profession, IProfessionListSelectorDialogCondition> {
    return this._source.$route.meta.isMobile
      ? this._multiOrderableListSelectors.mobile
      : this._multiOrderableListSelectors.default;
  }
}

class PostalAddressDialogs {
  constructor(source: Vue) {
    this.picker = source.$refs.postalAddressPickerDialog as PostalAddressPickerDialog;
  }
  public readonly picker: PostalAddressPickerDialog;
}

class WorkflowDialogs {
  constructor(source: Vue) {
    this.conflictCommentComparer = source.$refs.conflictCommentComparedDialog as ConflictCommentComparedDialog;
  }
  public readonly conflictCommentComparer: ConflictCommentComparedDialog;
}
// element-uiの部品ごとのimport対策でjsファイル経由でprototypeにセットしているため、
// VisualStudioCode上での型定義の拡張が自動で反映されない模様。手動で拡張している。
import { LoadingServiceOptions, ElLoadingComponent } from "element-ui/types/loading";
import { ElMessageBox, ElMessageBoxShortcutMethod } from "element-ui/types/message-box";
import { ElNotification } from "element-ui/types/notification";
import { ElMessage } from "element-ui/types/message";
declare module "vue/types/vue" {
  interface Vue {
    $openWindow: (location: string, parameters?: { [key: string]: string }) => void;
    $requestOpenWindow: (location: string, parameters?: { [key: string]: string }) => void;
    $wait: (isCompleted: () => boolean, pollingIntervalAsMsec?: number) => Promise<any>;
    $sleep: (ms: number) => Promise<void>;
    $success: (title: string, message: string, onClose?: () => void) => void;
    $successAsHTML: (title: string, message: string) => void;
    $info: (title: string, message: string) => void;
    $infoAsHTML: (title: string, message: string) => void;
    $warn: (title: string, message: string, onClose?: () => void) => void;
    $error: (title: string, message: string, reason?: any) => void;
    $errorAsHTML: (title: string, message: string, reason?: any) => void;
    $fatal: (title: string, message: string, reason?: any, timeoutAsMsec?: number) => void;
    $suspend: (title: string, message: string, reason?: any, timeoutAsMsec?: number) => void;
    $notImplemented: (message?: string) => void;
    $logger: ILogger;
    $measureTextDrawnSize: (text: string, textStyle: TextStyle) => Size;
    $printPdf: (source: string, copies?: number, shouldSelectPrinter?: boolean, shouldPrintWatermarkForcibly?: boolean, launchRequestKey?: string) => void;
    $printPdfTo: (source: string, printerName: string, copies?: number, launchRequestKey?: string) => void;
    $findAncestor: (predicate: (s: Vue) => boolean) => Vue;
    $doWhenValid: <T>(process: (i: T) => void, instance: T, ...instances: any[]) => void;
    $registerCloseConfirmation: (confirm: () => Promise<boolean>) => void;
    $registerDocumentRegisterAsTemporaryBeforeCloseHandler: (handler: (isForcibly: boolean) => Promise<void>) => void;
    $closeWindow: (isForcibly?: boolean) => Promise<void>;
    $registerCloseHandler: (process: (source: Document) => Promise<void>) => void;
    $listenGeometryHandling: (listener: (names: string[]) => void) => void;
    $storeGeometry: () => Promise<void>;
    $registerGeometryManager: <T extends IRectangle>(manager: IGeometryManageable<T>) => Promise<void>;
    $unregisterGeometryManager: <T extends IRectangle>(manager: IGeometryManageable<T>) => Promise<void>;
    $loading: (options: LoadingServiceOptions) => ElLoadingComponent;
    $msgbox: ElMessageBox;
    $alert: ElMessageBoxShortcutMethod;
    $confirm: ElMessageBoxShortcutMethod;
    $prompt: ElMessageBoxShortcutMethod;
    $notify: ElNotification;
    $message: ElMessage;
    $configuration: IConfiguration; // 拡張型への差し替えはinterface宣言では不可能.実装コンポーネントで$configuration!を拡張型に定義して使う事.
    $commonDialogs: CommonDialogs;
    $wording: IWordingConfiguration;
    $eraseImage: (rectangle: Rectangle, scale: number, source: string, hiddenElement: HTMLElement) => Promise<IDocumentImageSource>;
    $registerErasingChangedHandler: (process: (isErasing: boolean) => void) => void;
    $showTextInputContextMenu: (event: PointerEvent) => void;
    $redirect: (path: string, query?: { [key: string]: any }) => void;
    $redirectEntryRoute: (editPath?: (p: string) => string) => void;
    $sanitizeHtml: (dirty: string, options?: sanitizeHtml.IOptions) => string;
  }
}
